/**
 * UserAvatar — one avatar renderer for every people surface (FRIDAY-48).
 * Tries `sources` in order, then the email's Gravatar, then initials. Each
 * URL that fails to load is skipped for the lifetime of the component, so a
 * broken image can never cause an error loop.
 */

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { avatarCandidates, gravatarUrl, initialsFor } from './avatarSources'

export function UserAvatar({
  name,
  email,
  sources,
  className,
  pixelSize = 64,
  decorative = false,
}: {
  name: string
  email?: string | null
  /** Image URLs in precedence order (custom avatar first, provider avatar next). */
  sources: readonly (string | null | undefined)[]
  /** Size, shape and text-size classes, e.g. `h-6 w-6 text-[10px]`. */
  className?: string
  /** Requested Gravatar size; use about 2x the CSS size for sharp images. */
  pixelSize?: number
  /** Set when an adjacent label already names the person. */
  decorative?: boolean
}) {
  const [gravatar, setGravatar] = useState<string | null>(null)
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set())

  useEffect(() => {
    let live = true
    setGravatar(null)
    void gravatarUrl(email, pixelSize).then((url) => {
      if (live) setGravatar(url)
    })
    return () => {
      live = false
    }
  }, [email, pixelSize])

  const src = avatarCandidates([...sources, gravatar]).find((url) => !failed.has(url))
  const base = cn('inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full', className)

  if (src) {
    return (
      <img
        key={src}
        src={src}
        alt={decorative ? '' : name}
        data-avatar-source={src === gravatar ? 'gravatar' : 'image'}
        referrerPolicy="no-referrer"
        className={cn(base, 'object-cover')}
        onError={() => setFailed((prev) => new Set(prev).add(src))}
      />
    )
  }
  return (
    <span
      className={cn(base, 'bg-brand-500 font-semibold text-white')}
      data-avatar-source="initials"
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
    >
      {initialsFor(name)}
    </span>
  )
}
